import React, { Dispatch, SetStateAction, createContext, useContext, useState } from 'react';

import * as amplitude from '@amplitude/analytics-browser';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FullScreenLoader } from 'components/FullScreenLoader';
import { config } from 'environment/config';
import LogRocket from 'logrocket';
import { useNavigate } from 'react-router';
import { LanguageService } from 'services/language-service';
import { NotificationService } from 'services/notification-service';
import { PosthogService } from 'services/posthog-service';
import { SubscriptionService } from 'services/subscription-service';
import { UserService } from 'services/user-service';
import { EMPTY_USER } from 'shared/constants/empty-user';
import { USER_INFO_QUERY_KEY } from 'shared/constants/query-keys';
import { ROUTE_LINKS } from 'shared/constants/route-links';
import { Subscription } from 'shared/interfaces/Subscription';
import { User } from 'shared/interfaces/User';

const UserContext = createContext<[User, Dispatch<SetStateAction<User>>]>([
  EMPTY_USER,
  () => {
    // Default User
  }
]);

export function useUserContext() {
  return useContext(UserContext);
}

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [stateUser, setStateUser] = useState<User>(EMPTY_USER);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const fetchUserQuery = useQuery({
    queryKey: [USER_INFO_QUERY_KEY],
    queryFn: UserService.userInfo,
    onSuccess: async (user: User) => {
      setStateUser(user);

      // We do not need to set up third party analytics for development
      if (config.isDev) return;

      const userSubscription = await SubscriptionService.getSubscription();

      initThirdPartyAnalytics(userSubscription);
      setThirdPartyUserProfile(user, userSubscription);
    },
    onError: () => {
      UserService.logout();
      navigate(ROUTE_LINKS.login);
      NotificationService.showResult(false, 'AUTH.USER.ERROR');
    }
  });

  const initThirdPartyAnalytics = async (userSubscription: Subscription) => {
    // This is the public API key on PostHog. It is safe to be exposed.
    PosthogService.init();

    // Initialize LogRocket only for paid users because fewer sessions
    if (userSubscription.product.price > 0) {
      LogRocket.init('t9qrki/app');
    }
  };

  const setThirdPartyUserProfile = (user: User, userSubscription: Subscription) => {
    amplitude.setUserId(user._id);
    LogRocket.identify(user.email, {
      id: user._id,
      email: user.email,
      name: user.firstName + ' ' + user.lastName,
      organisation: user.organisation,
      productName: userSubscription.product.name,
      productPrice: userSubscription.product.price,
      isCustomOrgMember: UserService.isCustomOrganisationMember(),
      locale: LanguageService.getCurrentLanguage(),
      isDev: config.isDev
    });
    PosthogService.identify(user.email, {
      id: user._id,
      email: user.email,
      name: user.firstName + ' ' + user.lastName,
      organisation: user.organisation,
      productName: userSubscription.product.name,
      productPrice: userSubscription.product.price,
      isCustomOrgMember: UserService.isCustomOrganisationMember(),
      locale: LanguageService.getCurrentLanguage(),
      isDev: config.isDev,
      isGuest: false
    });
  };

  const setUser: Dispatch<SetStateAction<User>> = (newUser: User | ((prevUser: User) => User)) => {
    queryClient.setQueryData([USER_INFO_QUERY_KEY], (prevData?: User) => {
      const updatedUser = typeof newUser === 'function' ? newUser(prevData ?? EMPTY_USER) : newUser;
      return updatedUser === undefined ? prevData : updatedUser;
    });
    setStateUser((prevUser) => {
      const updatedUser = typeof newUser === 'function' ? newUser(prevUser) : newUser;
      return updatedUser === undefined ? prevUser : updatedUser;
    });
  };

  if (fetchUserQuery.isLoading) return <FullScreenLoader></FullScreenLoader>;

  return <UserContext.Provider value={[stateUser, setUser]}>{children}</UserContext.Provider>;
};
